"use client";

/** Session state for the signed-in learner, backed by the TargetQA API. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, TOKEN_KEY, USER_KEY, onUnauthorized, type ApiUser } from "@/lib/api/client";
import { authApi, invalidateBookmarksCache, invalidateChallengeProgressCache, invalidateNotificationsCache } from "@/lib/api/endpoints";
import { resetPreferencesSync } from "@/lib/challenges/preferences";
import { resetNotificationsSync } from "@/lib/notifications/context";

type Status = "loading" | "authenticated" | "anonymous";

interface SessionValue {
  status: Status;
  user: ApiUser | null;
  signIn(email: string, password: string): Promise<ApiUser>;
  signInSocial(payload: { provider: string; email: string; first_name?: string; last_name?: string; id_token?: string }): Promise<ApiUser>;
  signOut(): void;
  setUser(user: ApiUser): void;
}

const SessionContext = createContext<SessionValue | null>(null);

function readStoredUser(): ApiUser | null {
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as ApiUser) : null;
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("anonymous");
  const [user, setUserState] = useState<ApiUser | null>(null);

  const setUser = useCallback((next: ApiUser) => {
    window.localStorage.setItem(USER_KEY, JSON.stringify(next));
    setUserState(next);
    setStatus("authenticated");
  }, []);

  const signOut = useCallback(() => {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(USER_KEY);
    invalidateChallengeProgressCache();
    invalidateBookmarksCache();
    invalidateNotificationsCache();
    resetPreferencesSync();
    resetNotificationsSync();
    setUserState(null);
    setStatus("anonymous");
  }, []);

  useEffect(() => {
    // Storage is only readable after hydration; apply the stored session asynchronously.
    const token = window.localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    const cached = readStoredUser();
    if (cached) {
      void Promise.resolve().then(() => {
        setUserState(cached);
        setStatus("authenticated");
      });
    }

    // Safety timeout: ensure loading state never hangs indefinitely in production
    const safetyTimer = setTimeout(() => {
      setStatus((current) => (current === "loading" ? "anonymous" : current));
    }, 4000);

    // Revalidate the token; an expired one signs the learner out.
    authApi.me().then(
      (verifiedUser) => {
        clearTimeout(safetyTimer);
        setUser(verifiedUser);
      },
      (error: unknown) => {
        clearTimeout(safetyTimer);
        if (error instanceof ApiError && error.status === 401) {
          signOut();
        } else if (!cached) {
          setStatus("anonymous");
        }
      },
    );
    const unregister = onUnauthorized(signOut);
    return () => {
      clearTimeout(safetyTimer);
      unregister();
    };
  }, [setUser, signOut]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { user: signedIn, token } = await authApi.login(email, password);
      window.localStorage.setItem(TOKEN_KEY, token);
      setUser(signedIn);
      return signedIn;
    },
    [setUser],
  );

  const signInSocial = useCallback(
    async (payload: { provider: string; email: string; first_name?: string; last_name?: string; id_token?: string }) => {
      const { user: signedIn, token } = await authApi.social(payload);
      window.localStorage.setItem(TOKEN_KEY, token);
      setUser(signedIn);
      return signedIn;
    },
    [setUser],
  );

  const value = useMemo(
    () => ({ status, user, signIn, signInSocial, signOut, setUser }),
    [status, user, signIn, signInSocial, signOut, setUser],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}

export function displayName(user: ApiUser | null): string {
  if (!user) return "";
  const name = `${user.first_name} ${user.last_name}`.trim();
  return name || user.email.split("@")[0];
}

export function initials(user: ApiUser | null): string {
  const name = displayName(user);
  return name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "?";
}
